// Library state (§8, §10): the opened folder, its photos, ratings/reject, selection, filter, cull view.
import { create } from 'zustand';
import type { PhotoMeta } from '../model/types';
import type { PhotoFolder } from '../io/fs';

export type LibraryPhoto = PhotoMeta & {
  file: File;
  /** A sidecar exists but belongs to a different version of the file (size/lastModified differ). */
  sidecarMismatch?: boolean;
  /** A sidecar exists that this build refused (newer schema, another app, unreadable): never overwritten. */
  sidecarLocked?: boolean;
  edited?: boolean;
  bgRemoved?: boolean;
};

export type Filter = 'all' | 'picks' | 'rejected' | 'edited' | 1 | 2 | 3 | 4 | 5;

export type LibraryState = {
  folder: PhotoFolder | null;
  folderName: string | null;
  photos: LibraryPhoto[];
  activeId: string | null;
  selected: Set<string>;
  anchorId: string | null;
  filter: Filter;
  cull: boolean;
  autoAdvance: boolean;
  setFolder: (folder: PhotoFolder | null, photos: LibraryPhoto[]) => void;
  /** Kept for M3 callers and tests: opens files without a folder. */
  setPhotos: (photos: LibraryPhoto[], folderName: string | null) => void;
  setActive: (id: string | null) => void;
  step: (delta: number) => void;
  patch: (id: string, p: Partial<LibraryPhoto>) => void;
  click: (id: string, mods: { shift?: boolean; ctrl?: boolean }) => void;
  selectAll: () => void;
  setFilter: (f: Filter) => void;
  rate: (ids: string[], rating: PhotoMeta['rating']) => void;
  toggleReject: (ids: string[]) => void;
  set: (p: Partial<Pick<LibraryState, 'cull' | 'autoAdvance'>>) => void;
};

export const photoId = (f: { name: string; size: number; lastModified: number }) =>
  `${f.name}|${f.size}|${f.lastModified}`;

export function passesFilter(p: LibraryPhoto, f: Filter): boolean {
  if (f === 'all') return true;
  if (f === 'picks') return !p.rejected;
  if (f === 'rejected') return p.rejected;
  if (f === 'edited') return !!p.edited;
  return p.rating >= f;
}

/** The photos currently visible in the filmstrip, in order. */
export const visiblePhotos = (s: Pick<LibraryState, 'photos' | 'filter'>) =>
  s.photos.filter((p) => passesFilter(p, s.filter));

export const useLibrary = create<LibraryState>((set, get) => ({
  folder: null,
  folderName: null,
  photos: [],
  activeId: null,
  selected: new Set(),
  anchorId: null,
  filter: 'all',
  cull: false,
  autoAdvance: true,
  setFolder: (folder, photos) =>
    set({
      folder,
      folderName: folder?.name ?? null,
      photos,
      activeId: photos[0]?.id ?? null,
      selected: new Set(photos[0] ? [photos[0].id] : []),
      anchorId: photos[0]?.id ?? null,
      filter: 'all',
    }),
  setPhotos: (photos, folderName) =>
    set({
      folder: null,
      folderName,
      photos,
      activeId: photos[0]?.id ?? null,
      selected: new Set(photos[0] ? [photos[0].id] : []),
      anchorId: photos[0]?.id ?? null,
    }),
  setActive: (id) => set({ activeId: id, selected: new Set(id ? [id] : []), anchorId: id }),
  step: (delta) => {
    const st = get();
    const vis = visiblePhotos(st);
    if (!vis.length) return;
    const i = Math.max(
      0,
      vis.findIndex((p) => p.id === st.activeId),
    );
    const j = Math.min(vis.length - 1, Math.max(0, i + delta));
    get().setActive(vis[j]!.id);
  },
  patch: (id, p) => set((st) => ({ photos: st.photos.map((x) => (x.id === id ? { ...x, ...p } : x)) })),
  click: (id, mods) => {
    const st = get();
    if (mods.shift && st.anchorId) {
      const vis = visiblePhotos(st);
      const a = vis.findIndex((p) => p.id === st.anchorId);
      const b = vis.findIndex((p) => p.id === id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        const sel = new Set(mods.ctrl ? st.selected : []);
        for (let i = lo; i <= hi; i++) sel.add(vis[i]!.id);
        set({ selected: sel, activeId: id });
        return;
      }
    }
    if (mods.ctrl) {
      const sel = new Set(st.selected);
      if (sel.has(id) && sel.size > 1) sel.delete(id);
      else sel.add(id);
      set({ selected: sel, activeId: id, anchorId: id });
      return;
    }
    get().setActive(id);
  },
  selectAll: () => set((st) => ({ selected: new Set(visiblePhotos(st).map((p) => p.id)) })),
  setFilter: (filter) => set({ filter }),
  rate: (ids, rating) =>
    set((st) => ({ photos: st.photos.map((p) => (ids.includes(p.id) ? { ...p, rating } : p)) })),
  toggleReject: (ids) =>
    set((st) => ({
      photos: st.photos.map((p) => (ids.includes(p.id) ? { ...p, rejected: !p.rejected } : p)),
    })),
  set: (p) => set(p),
}));

export function toLibraryPhotos(files: File[]): LibraryPhoto[] {
  return files.map((file) => ({
    id: photoId(file),
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
    width: 0,
    height: 0,
    orientation: 1,
    rating: 0,
    rejected: false,
    hasSidecar: false,
    file,
  }));
}
